import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Badge, Button, Flex } from '@patternfly/react-core';
import { useTranslation } from 'react-i18next';
import { useFeature } from '@ole-ui/ole-ui-components';
import { EnablementFeatureFlags } from 'config/featureFlags';
import { storesContext } from 'stores';
import { useFeatureFlagManager } from 'components/FeatureFlagManager';
import { isLocalhost } from 'helpers/utils';

function FeatureFlagManagerMenuButton({ setMenuOpen }) {
  const { openModal } = useFeatureFlagManager();

  return (
    <Button
      variant="link"
      className="button-link"
      onClick={() => {
        setMenuOpen(false);
        openModal();
      }}
      data-analytics-id="feature-flag-manager-link-header-lp"
    >
      Feature flag manager
    </Button>
  );
}

FeatureFlagManagerMenuButton.propTypes = {
  setMenuOpen: PropTypes.func.isRequired,
};

function AccountMenu({ setMenuOpen, onLogout }) {
  const { t } = useTranslation();
  const { userNotificationsStore, userStore, routerStore, eventStore } =
    useContext(storesContext);
  const { user, isAdmin, isInstructor, username, isSuperuser } = userStore;
  const { preferred_name: preferredName } = user;

  const notificationCount =
    eventStore.newNotifications.length +
      userNotificationsStore?.newUserNotifications?.length || 0;

  const isInstructorDashboardV2Enabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.InstructorDashboardEnabled,
    defaultFlagValue: false,
  });

  const isOldInstructorDashboardEnabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.OldInstructorDashboardEnabled,
    defaultFlagValue: false,
  });

  return (
    <Flex
      className="account-menu-container"
      justifyContent={{ default: 'justifyContentSpaceEvenly' }}
    >
      <Flex
        direction={{ default: 'column' }}
        alignItems={{ default: 'alignItemsFlexStart' }}
      >
        <h3>
          {t('Welcome, {{preferredName}}', {
            preferredName: preferredName || username,
          })}
        </h3>
        <Button
          variant="link"
          className="button-link"
          onClick={() => {
            setMenuOpen(false);
            routerStore.navigate('account.profile');
          }}
          data-analytics-id="profile-page-link-header-lp"
        >
          {t('Profile')}
        </Button>
        <Button
          variant="link"
          className="button-link"
          onClick={() => {
            setMenuOpen(false);
            eventStore.toggleDrawer('all');
          }}
          data-analytics-id="notification-icon-header-lp"
        >
          <React.Fragment>
            {t('Notifications')}

            {!!notificationCount && (
              <Badge className="notification-badge">{notificationCount}</Badge>
            )}
          </React.Fragment>
        </Button>
        {(isAdmin || isInstructor) && isOldInstructorDashboardEnabled && (
          <Button
            variant="link"
            className="button-link"
            onClick={() => {
              setMenuOpen(false);
              routerStore.navigate('classes:list');
            }}
            data-analytics-id="instructor-dashboard-link-header-lp"
          >
            {t('Instructor Dashboard')}
          </Button>
        )}
        {(isAdmin || isInstructor) && isInstructorDashboardV2Enabled && (
          <Button
            variant="link"
            className="button-link"
            onClick={() => {
              setMenuOpen(false);
              routerStore.navigate('instructor-dashboard:class-list');
            }}
            data-analytics-id="instructor-dashboard-v2-link-header-lp"
          >
            {t('Instructor Dashboard')}
          </Button>
        )}
        {isAdmin && (
          <Button
            variant="link"
            className="button-link"
            onClick={() => {
              setMenuOpen(false);
              routerStore.navigate('admin');
            }}
            data-analytics-id="admin-dashboard-link-header-lp"
          >
            {t('Admin Dashboard')}
          </Button>
        )}
        {(isLocalhost || isSuperuser) && (
          <FeatureFlagManagerMenuButton setMenuOpen={setMenuOpen} />
        )}
      </Flex>
      <Flex
        direction={{ default: 'column' }}
        alignItems={{ default: 'alignItemsFlexStart' }}
        alignSelf={{ default: 'alignSelfCenter' }}
      >
        <p className="logout-text">
          {t(
            'For your security, if you are on a public computer and have finished using your Red Hat services, please log out.',
          )}
        </p>
        <Button
          variant="danger"
          onClick={() => {
            setMenuOpen(false);
            onLogout();
          }}
          data-analytics-id="logout-link-header-lp"
        >
          {t('Logout')}
        </Button>
      </Flex>
    </Flex>
  );
}

AccountMenu.propTypes = {
  setMenuOpen: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
};

export default AccountMenu;
